/**
 * Art directions, as token sets.
 *
 * A theme is only data — colour, type, radius, easing — which is why the app
 * can carry three of them and why the admin panel can switch between them at
 * runtime without touching a single component. Arena was cut after the Phase 0
 * bake-off; these three shipped.
 */
export type ThemeId = 'playground' | 'paper' | 'clay';

/**
 * How a selected tile announces itself. Playground inverts figure and ground —
 * the tile takes the ink colour and the numeral takes the tile's own colour —
 * because on a wall of bright candy tiles a mere tint change is easy to lose.
 */
export type SelectionStyle = 'invert' | 'circle' | 'press';

export interface ThemeTokens {
  bg: string;
  surface: string;
  ink: string;
  muted: string;
  line: string;
  tile: string;
  tileInk: string;
  sel: string;
  good: string;
  bad: string;
  accent: string;
  /**
   * Ink that sits ON a filled accent / error / selection surface.
   *
   * These exist because a light accent (Clay's mint) and a dark one (Paper's
   * red) cannot share one hard-coded foreground — the mint button with white
   * text was unreadable, which is exactly the bug these tokens close.
   */
  onAccent: string;
  onBad: string;
  onSel: string;
  /** Tile palette. Playground and Clay cycle these across the grid. */
  c1: string;
  c2: string;
  c3: string;
  c4: string;
  tileShadow: string;
}

export interface Theme {
  id: ThemeId;
  nameKey: string;
  descKey: string;
  fontLatin: string;
  fontArabic: string;
  /** Google Fonts families this theme needs loaded. */
  fontFamilies: string[];
  radius: string;
  tileRadius: string;
  ease: string;
  durationMs: number;
  weight: number;
  selectionStyle: SelectionStyle;
  /** Cycle the c1–c4 palette across tiles instead of one flat tile colour. */
  multicolourTiles: boolean;
  light: ThemeTokens;
  dark: ThemeTokens;
}

export const THEMES: Record<ThemeId, Theme> = {
  playground: {
    id: 'playground',
    nameKey: 'theme.playground',
    descKey: 'theme.playground.desc',
    fontLatin: '"Baloo Bhaijaan 2"',
    fontArabic: '"Baloo Bhaijaan 2"',
    fontFamilies: ['Baloo+Bhaijaan+2:wght@600;700;800'],
    radius: '28px',
    tileRadius: '999px',
    ease: 'cubic-bezier(.34,1.56,.64,1)',
    durationMs: 420,
    weight: 800,
    selectionStyle: 'invert',
    multicolourTiles: true,
    light: {
      bg: '#fff4e9', surface: '#ffffff', ink: '#4a3527', muted: '#9a7f6b', line: '#f2ddc8',
      tile: '#ffffff', tileInk: '#4a3527', sel: '#4a3527', good: '#3fbf8f', bad: '#ff8fa3',
      accent: '#ff6b6b', onAccent: '#3a2418', onBad: '#4a3527', onSel: '#fff4e9',
      c1: '#ffd166', c2: '#ff8fa3', c3: '#8ad9c4', c4: '#9bb8ff',
      tileShadow: '0 6px 0 rgba(74,53,39,.16)',
    },
    dark: {
      bg: '#2a1f2d', surface: '#37293b', ink: '#ffeede', muted: '#b79aa8', line: '#4a3850',
      tile: '#42314a', tileInk: '#2a1f2d', sel: '#ffeede', good: '#4fd6a0', bad: '#ff8fa3',
      accent: '#ff8080', onAccent: '#2a1f2d', onBad: '#2a1f2d', onSel: '#2a1f2d',
      c1: '#ffc861', c2: '#f781a0', c3: '#6fcdb5', c4: '#8fadf5',
      tileShadow: '0 6px 0 rgba(0,0,0,.35)',
    },
  },

  paper: {
    id: 'paper',
    nameKey: 'theme.paper',
    descKey: 'theme.paper.desc',
    fontLatin: '"Caveat"',
    fontArabic: '"Aref Ruqaa"',
    fontFamilies: ['Caveat:wght@600;700', 'Aref+Ruqaa:wght@400;700'],
    radius: '6px',
    tileRadius: '4px',
    ease: 'cubic-bezier(.22,.61,.36,1)',
    durationMs: 500,
    weight: 700,
    selectionStyle: 'circle',
    multicolourTiles: false,
    light: {
      bg: '#fdfcf7', surface: '#fffef9', ink: '#1f3a93', muted: '#7d86a8', line: '#cfe0ef',
      tile: 'transparent', tileInk: '#1f3a93', sel: '#d92626', good: '#d92626', bad: '#c2410c',
      accent: '#d92626', onAccent: '#fffef9', onBad: '#fffef9', onSel: '#fffef9',
      c1: '#1f3a93', c2: '#d92626', c3: '#cfe0ef', c4: '#e8a9a9',
      tileShadow: 'none',
    },
    // Not an inversion — the dark counterpart of a notebook is a chalkboard.
    dark: {
      bg: '#1c2620', surface: '#22302a', ink: '#f2f0e6', muted: '#8fa396', line: '#33463b',
      tile: 'transparent', tileInk: '#f2f0e6', sel: '#ff8a7a', good: '#ff8a7a', bad: '#e0a35c',
      accent: '#ff8a7a', onAccent: '#1c2620', onBad: '#1c2620', onSel: '#1c2620',
      c1: '#f2f0e6', c2: '#ff8a7a', c3: '#33463b', c4: '#c9d6b8',
      tileShadow: 'none',
    },
  },

  clay: {
    id: 'clay',
    nameKey: 'theme.clay',
    descKey: 'theme.clay.desc',
    fontLatin: '"Fredoka"',
    fontArabic: '"Cairo"',
    fontFamilies: ['Fredoka:wght@500;600;700', 'Cairo:wght@600;700;900'],
    radius: '24px',
    tileRadius: '20px',
    ease: 'cubic-bezier(.4,0,.2,1)',
    durationMs: 340,
    weight: 600,
    selectionStyle: 'press',
    multicolourTiles: false,
    light: {
      bg: '#e8e6f0', surface: '#f3f1fa', ink: '#4a4363', muted: '#8b84a8', line: '#d9d5e8',
      tile: '#f0eef8', tileInk: '#4a4363', sel: '#b3a4e8', good: '#7fd1b9', bad: '#f7a4a4',
      accent: '#7fd1b9', onAccent: '#173d34', onBad: '#5a2727', onSel: '#241c4d',
      c1: '#7fd1b9', c2: '#f7a4a4', c3: '#b3a4e8', c4: '#f5d78e',
      tileShadow: '6px 6px 12px #cdc9dd, -6px -6px 12px #ffffff',
    },
    dark: {
      bg: '#2b2838', surface: '#353149', ink: '#efecfa', muted: '#a49cc4', line: '#413c5c',
      tile: '#3a3552', tileInk: '#efecfa', sel: '#b3a4e8', good: '#7fd1b9', bad: '#f7a4a4',
      accent: '#7fd1b9', onAccent: '#12312a', onBad: '#4a1f1f', onSel: '#241c4d',
      c1: '#7fd1b9', c2: '#f7a4a4', c3: '#b3a4e8', c4: '#f5d78e',
      tileShadow: '6px 6px 14px #211f2c, -6px -6px 14px #443f60',
    },
  },
};

export const THEME_IDS = Object.keys(THEMES) as ThemeId[];

export const DEFAULT_THEME: ThemeId = 'playground';

export function isThemeId(v: unknown): v is ThemeId {
  return typeof v === 'string' && v in THEMES;
}

/**
 * Age-adaptive default, kept from the Phase 0 fifth option: the youngest
 * grades get the gentlest world unless a teacher pins a theme for the class.
 */
export function themeForGrade(grade: number): ThemeId {
  if (grade <= 2) return 'playground';
  if (grade <= 4) return 'clay';
  return 'paper';
}

/** Flattens a theme into the CSS custom properties the app actually styles with. */
export function themeCssVars(theme: Theme, mode: 'light' | 'dark', rtl: boolean): Record<string, string> {
  const t = mode === 'dark' ? theme.dark : theme.light;
  return {
    '--g-bg': t.bg,
    '--g-surface': t.surface,
    '--g-ink': t.ink,
    '--g-muted': t.muted,
    '--g-line': t.line,
    '--g-tile': t.tile,
    '--g-tile-ink': t.tileInk,
    '--g-sel': t.sel,
    '--g-good': t.good,
    '--g-bad': t.bad,
    '--g-accent': t.accent,
    '--g-on-accent': t.onAccent,
    '--g-on-bad': t.onBad,
    '--g-on-sel': t.onSel,
    '--g-c1': t.c1,
    '--g-c2': t.c2,
    '--g-c3': t.c3,
    '--g-c4': t.c4,
    '--g-tile-shadow': t.tileShadow,
    '--g-radius': theme.radius,
    '--g-tile-radius': theme.tileRadius,
    '--g-ease': theme.ease,
    '--g-dur': `${theme.durationMs}ms`,
    '--g-weight': String(theme.weight),
    '--g-font': `${rtl ? theme.fontArabic : theme.fontLatin}, system-ui, sans-serif`,
  };
}
